import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const GALLERY_SECTION_TITLES = new Set(['gallery', 'galeria']);

const normalizeLabel = (value = '') => String(value)
  .toLowerCase()
  .trim()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

const getHeadingLevel = (element) => {
  if (!element?.tagName || !/^H[1-6]$/.test(element.tagName)) {
    return null;
  }

  return Number(element.tagName.slice(1));
};

const resolveContentRoot = (anchorElement) => {
  const anchoredRoot = anchorElement?.closest?.('.editorial-prose.gh-content, .editorial-prose');

  if (anchoredRoot) {
    return anchoredRoot;
  }

  const selectors = ['.editorial-prose.gh-content', '.editorial-prose'];

  for (const selector of selectors) {
    const element = document.querySelector(selector);

    if (element) {
      return element;
    }
  }

  return null;
};

const nodeHasGalleryImages = (node) => {
  if (!node) return false;

  if (node.matches?.('img, figure, .kg-image-card, .kg-gallery-card, .kg-width-wide, .kg-width-full')) {
    return true;
  }

  return Boolean(node.querySelector?.('img, figure, .kg-image-card, .kg-gallery-card'));
};

const nodeHasMeaningfulText = (node) => {
  if (!node) return false;
  if (nodeHasGalleryImages(node)) return false;

  const text = String(node.textContent || '').replace(/\s+/g, ' ').trim();
  return text.length > 0;
};

const findGalleryBoundary = (root) => {
  if (!root) {
    return null;
  }

  const explicitBoundary = root.querySelector('[data-gallery-boundary]');

  if (explicitBoundary) {
    const explicitHeading = explicitBoundary.querySelector('h1, h2, h3, h4, h5, h6');
    return {
      title: explicitBoundary.getAttribute('data-gallery-title') || explicitHeading?.textContent?.trim() || 'Gallery',
      nodes: [explicitBoundary]
    };
  }

  const headings = Array.from(root.querySelectorAll('h1, h2, h3, h4, h5, h6'));

  for (const heading of headings) {
    if (!GALLERY_SECTION_TITLES.has(normalizeLabel(heading.textContent))) {
      continue;
    }

    const nodes = [heading];
    const startLevel = getHeadingLevel(heading);
    let hasGalleryMedia = false;
    let sibling = heading.nextElementSibling;

    while (sibling) {
      const siblingLevel = getHeadingLevel(sibling);

      if (siblingLevel && startLevel && siblingLevel <= startLevel) {
        break;
      }

      if (nodeHasGalleryImages(sibling)) {
        nodes.push(sibling);
        hasGalleryMedia = true;
        sibling = sibling.nextElementSibling;
        continue;
      }

      // Stop once gallery media has started and we hit normal prose/content.
      if (hasGalleryMedia && nodeHasMeaningfulText(sibling)) {
        break;
      }

      sibling = sibling.nextElementSibling;
    }

    if (!hasGalleryMedia) {
      continue;
    }

    return {
      title: heading.textContent.trim() || 'Gallery',
      nodes
    };
  }

  return null;
};

const parseGalleryNodes = (nodes, postTitle) => {
  const items = [];
  const seenSources = new Set();
  let fallbackOrder = 0;

  const pushImage = (img, figcaption) => {
    const src = img?.getAttribute('src');

    if (!src || seenSources.has(src)) {
      return;
    }

    seenSources.add(src);

    const captionText = figcaption?.textContent?.trim() || img.getAttribute('title') || img.getAttribute('alt') || `${postTitle} photo ${fallbackOrder + 1}`;
    const orderValue = Number.parseInt(figcaption?.getAttribute('data-order') || img.getAttribute('data-order') || `${fallbackOrder}`, 10);
    const credit = figcaption?.getAttribute('data-credit') || img.getAttribute('data-credit') || img.dataset.credit || '';

    items.push({
      id: `${src}-${fallbackOrder}`,
      src,
      alt: img.getAttribute('alt') || captionText,
      caption: captionText,
      credit,
      order: Number.isNaN(orderValue) ? fallbackOrder : orderValue
    });

    fallbackOrder += 1;
  };

  nodes.forEach((node) => {
    if (!node || /^H[1-6]$/.test(node.tagName || '')) {
      return;
    }

    const figures = node.matches?.('figure') ? [node] : Array.from(node.querySelectorAll?.('figure') || []);
    const figureImages = new Set();

    figures.forEach((figure) => {
      const img = figure.querySelector('img');

      if (!img) {
        return;
      }

      figureImages.add(img);
      pushImage(img, figure.querySelector('figcaption'));
    });

    const standaloneImages = node.matches?.('img') ? [node] : Array.from(node.querySelectorAll?.('img') || []);

    standaloneImages.forEach((img) => {
      if (!figureImages.has(img)) {
        pushImage(img, null);
      }
    });
  });

  return items.sort((left, right) => left.order - right.order);
};

const collectSourceNodesToHide = (nodes = []) => {
  const seen = new Set();
  const targets = [];

  const push = (node) => {
    if (!node || seen.has(node)) {
      return;
    }

    seen.add(node);
    targets.push(node);
  };

  nodes.forEach((node) => {
    if (!node) {
      return;
    }

    if (/^H[1-6]$/.test(node.tagName || '')) {
      push(node);
    }

    if (node.matches?.('figure, .kg-image-card, .kg-gallery-card, .kg-gallery-container, .kg-gallery-row, .kg-gallery-image, img')) {
      push(node);
    }

    const mediaNodes = Array.from(
      node.querySelectorAll?.('figure, .kg-image-card, .kg-gallery-card, .kg-gallery-container, .kg-gallery-row, .kg-gallery-image, img') || []
    );

    mediaNodes.forEach(push);
  });

  return targets;
};

const ShowGalleryCarousel = ({ postTitle = 'Show' }) => {
  const [images, setImages] = useState([]);
  const [sectionTitle, setSectionTitle] = useState('Gallery');
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const mountSectionRef = useRef(null);
  const hiddenSourceNodesRef = useRef([]);
  const slideRefs = useRef([]);
  const railRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeftState, setScrollLeftState] = useState(0);
  const [dragTravel, setDragTravel] = useState(0);

  const handleMouseDown = (e) => {
    if (!railRef.current) return;
    setIsDragging(true);
    setDragTravel(0);
    setStartX(e.pageX - railRef.current.offsetLeft);
    setScrollLeftState(railRef.current.scrollLeft);
    railRef.current.style.scrollBehavior = 'auto';
    railRef.current.style.scrollSnapType = 'none';
  };

  const handleMouseLeave = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (railRef.current) {
        railRef.current.style.scrollSnapType = 'x mandatory';
        railRef.current.style.scrollBehavior = 'smooth';
    }
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (railRef.current) {
        railRef.current.style.scrollSnapType = 'x mandatory';
        railRef.current.style.scrollBehavior = 'smooth';
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !railRef.current) return;
    e.preventDefault();
    const x = e.pageX - railRef.current.offsetLeft;
    const walk = (x - startX) * 2; 
    setDragTravel(Math.abs(walk));
    railRef.current.scrollLeft = scrollLeftState - walk;
  };

  useEffect(() => {
    const contentRoot = resolveContentRoot(mountSectionRef.current);
    const boundary = findGalleryBoundary(contentRoot);

    hiddenSourceNodesRef.current.forEach(({ node, display, ariaHidden }) => {
      if (display) {
        node.style.display = display;
      } else {
        node.style.removeProperty('display');
      }

      if (ariaHidden === null) {
        node.removeAttribute('aria-hidden');
      } else {
        node.setAttribute('aria-hidden', ariaHidden);
      }

      node.classList.remove('show-gallery-source-hidden');
    });
    hiddenSourceNodesRef.current = [];

    if (!boundary) {
      setImages([]);
      return undefined;
    }

    const parsedImages = parseGalleryNodes(boundary.nodes, postTitle);

    if (!parsedImages.length) {
      setImages([]);
      return undefined;
    }

    setSectionTitle(boundary.title || 'Gallery');
    setImages(parsedImages);
    setCurrentIndex(0);

    const sourceNodesToHide = collectSourceNodesToHide(boundary.nodes);
    hiddenSourceNodesRef.current = sourceNodesToHide.map((node) => ({
      node,
      display: node.style.display,
      ariaHidden: node.getAttribute('aria-hidden')
    }));

    hiddenSourceNodesRef.current.forEach(({ node }) => {
      node.classList.add('show-gallery-source-hidden');
      node.style.setProperty('display', 'none', 'important');
      node.setAttribute('aria-hidden', 'true');
    });

    return () => {
      hiddenSourceNodesRef.current.forEach(({ node, display, ariaHidden }) => {
        if (display) {
          node.style.display = display;
        } else {
          node.style.removeProperty('display');
        }

        if (ariaHidden === null) {
          node.removeAttribute('aria-hidden');
        } else {
          node.setAttribute('aria-hidden', ariaHidden);
        }

        node.classList.remove('show-gallery-source-hidden');
      });

      hiddenSourceNodesRef.current = [];
    };
  }, [postTitle]);

  useEffect(() => {
    const mountContainer = mountSectionRef.current?.parentElement;

    if (!mountContainer) {
      return undefined;
    }

    mountContainer.classList.add('show-gallery-breakout');

    return () => {
      mountContainer.classList.remove('show-gallery-breakout');
    };
  }, [images.length]);

  useEffect(() => {
    if (!images.length) {
      return;
    }

    slideRefs.current[currentIndex]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  }, [currentIndex, images.length]);

  useEffect(() => {
    if (!selectedImage) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedImage(null);
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [selectedImage]);

  if (!images.length) {
    return null;
  }

  const hasMultipleImages = images.length > 1;

  const openImage = (image, index) => {
    setCurrentIndex(index);
    setSelectedImage({
      ...image,
      showName: postTitle,
      title: image.caption,
      photographer: image.credit,
      category: sectionTitle
    });
  };

  const goPrevious = () => {
    setCurrentIndex((index) => (index - 1 + images.length) % images.length);
  };

  const goNext = () => {
    setCurrentIndex((index) => (index + 1) % images.length);
  };

  return (
    <section ref={mountSectionRef} className="show-gallery-carousel" aria-label={sectionTitle}>
      <style>{`
        .show-gallery-breakout {
          position: relative;
          z-index: 2;
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          overflow-x: hidden;
          box-sizing: border-box;
        }

        .show-gallery-breakout > * {
          min-width: 0;
        }

        .show-gallery-carousel {
          --show-gallery-text: var(--theme-text, #0f172a);
          --show-gallery-muted: var(--theme-text-muted, #475569);
          margin: 2.4rem 0;
          color: var(--show-gallery-text);
          width: 100%;
          max-width: 100%;
          min-width: 0;
          overflow-x: hidden;
          box-sizing: border-box;
        }

        @media (min-width: 1201px) {
          .post-content-wrapper .main-content > .show-gallery-breakout {
            width: 100%;
            max-width: 100%;
            margin-right: 0;
          }
        }

        [data-theme="dark"] .show-gallery-carousel {
          --show-gallery-text: #f8fafc;
          --show-gallery-muted: #cbd5e1;
        }

        .show-gallery-header {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          margin-bottom: 1.25rem;
        }

        .show-gallery-title {
          margin: 0;
          font-size: clamp(1.55rem, 2.8vw, 2.15rem);
          line-height: 1.08;
          color: var(--show-gallery-text);
        }

        .show-gallery-shell {
          position: relative;
          width: 100%;
          max-width: 100%;
          min-width: 0;
          margin: 0 auto;
          box-sizing: border-box;
          padding: 0 4.4rem;
          overflow: hidden;
        }

        .show-gallery-arrow {
          appearance: none;
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 4;
          width: 56px;
          height: 56px;
          border: 1px solid rgba(255, 255, 255, 0.24);
          border-radius: 999px;
          background: rgba(7, 17, 32, 0.97);
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 16px 30px rgba(15, 23, 42, 0.28);
          transition: transform 0.25s ease, background 0.25s ease, border-color 0.25s ease;
        }

        .show-gallery-arrow:hover {
          transform: translateY(-50%) scale(1.07);
          background: rgba(15, 23, 42, 1);
          border-color: rgba(255, 255, 255, 0.28);
        }

        .show-gallery-arrow:focus-visible {
          outline: 2px solid #ffffff;
          outline-offset: 2px;
        }

        .show-gallery-arrow svg {
          width: 26px;
          height: 26px;
          display: block;
          stroke: currentColor;
          strokeWidth: 3.4;
          strokeLinecap: round;
          strokeLinejoin: round;
          fill: none;
          pointer-events: none;
        }

        .show-gallery-arrow-prev {
          left: max(0.25rem, env(safe-area-inset-left));
        }

        .show-gallery-arrow-next {
          right: max(0.25rem, env(safe-area-inset-right));
          display: inline-flex !important;
        }

        .show-gallery-rail {
          display: flex;
          flex-direction: row;
          gap: 1rem;
          width: 100%;
          max-width: 100%;
          min-width: 0;
          overflow-x: auto;
          padding: 0.2rem 0.2rem 0.9rem;
          scroll-snap-type: x mandatory;
          scroll-behavior: smooth;
          scrollbar-width: none;
          cursor: grab;
          -webkit-user-select: none;
          user-select: none;
        }

        .show-gallery-rail::after {
          content: '';
          flex: 0 0 0.35rem;
        }

        .show-gallery-rail:active {
          cursor: grabbing;
        }

        .show-gallery-rail::-webkit-scrollbar {
          display: none;
        }

        .show-gallery-slide {
          scroll-snap-align: start;
          flex-shrink: 0;
          width: clamp(220px, 22vw, 300px);
          display: flex;
        }

        .show-gallery-rail.is-large .show-gallery-slide {
          width: clamp(200px, 19vw, 260px);
        }

        .show-gallery-card {
          position: relative;
          display: block;
          width: 100%;
          height: clamp(250px, 24vw, 340px);
          border: 0;
          padding: 0;
          background-color: #f1f5f9;
          border-radius: 18px;
          overflow: hidden;
          cursor: inherit;
          transition: transform 0.35s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.08);
        }

        [data-theme="dark"] .show-gallery-card {
          background-color: #1e293b;
        }

        .show-gallery-card:hover,
        .show-gallery-card:focus-within {
          transform: translateY(-6px);
          box-shadow: 0 16px 36px rgba(15, 23, 42, 0.16);
        }

        .show-gallery-card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.6s cubic-bezier(0.25, 0.8, 0.25, 1), filter 0.45s ease;
          pointer-events: none;
        }

        .show-gallery-card:hover .show-gallery-card-image,
        .show-gallery-card:focus-within .show-gallery-card-image {
          transform: scale(1.04);
          filter: brightness(1.02);
        }

        .show-gallery-rail.is-single .show-gallery-slide {
          width: minmax(320px, 440px);
        }

        @media (max-width: 1024px) {
          .show-gallery-shell {
            padding: 0 3.8rem;
          }

          .show-gallery-slide {
            width: clamp(220px, 36vw, 310px);
          }

          .show-gallery-rail.is-large .show-gallery-slide {
            width: clamp(200px, 32vw, 280px);
          }

          .show-gallery-arrow {
            width: 52px;
            height: 52px;
          }

          .show-gallery-card {
            height: clamp(250px, 34vw, 330px);
          }
        }

        @media (max-width: 768px) {
          .show-gallery-shell {
            padding: 0 3.3rem;
          }

          .show-gallery-slide {
            width: clamp(220px, 62vw, 340px);
          }

          .show-gallery-arrow-prev {
            left: 0.15rem;
          }

          .show-gallery-arrow-next {
            right: 0.15rem;
          }
        }

        @media (max-width: 640px) {
          .show-gallery-shell {
            padding: 0 2.4rem;
          }

          .show-gallery-rail {
            gap: 0.9rem;
          }

          .show-gallery-slide {
            width: 74vw;
          }

          .show-gallery-rail.is-large .show-gallery-slide {
            width: 68vw;
          }

          .show-gallery-card {
            height: 280px;
          }

          .show-gallery-arrow {
            width: 48px;
            height: 48px;
          }

          .show-gallery-arrow svg {
            width: 22px;
            height: 22px;
          }
        }
      `}</style>

      <div className="show-gallery-header">
        <h2 className="show-gallery-title">{sectionTitle}</h2>
      </div>

      <div className="show-gallery-shell">
        {hasMultipleImages && (
          <>
            <button type="button" className="show-gallery-arrow show-gallery-arrow-prev" onClick={goPrevious} aria-label="Previous photo">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <button type="button" className="show-gallery-arrow show-gallery-arrow-next" onClick={goNext} aria-label="Next photo">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          </>
        )}

        <div 
          ref={railRef}
          className={`show-gallery-rail ${hasMultipleImages ? 'is-carousel' : 'is-single'} ${images.length > 12 ? 'is-large' : ''}`} 
          aria-live="polite"
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
        >
          {images.map((image, index) => (
            <div
              key={image.id}
              ref={(node) => {
                slideRefs.current[index] = node;
              }}
              className="show-gallery-slide"
            >
              <div
                role="button"
                tabIndex={0}
                className="show-gallery-card"
                onClick={(e) => {
                  if (dragTravel > 5) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  openImage(image, index);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openImage(image, index);
                  }
                }}
                aria-label={`Open ${image.caption}`}
              >
                <img className="show-gallery-card-image" src={image.src} alt={image.alt} loading="lazy" decoding="async" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedImage && <ShowGalleryLightbox image={selectedImage} onClose={() => setSelectedImage(null)} />}
    </section>
  );
};

const ShowGalleryLightbox = React.memo(({ image, onClose }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [portalTarget, setPortalTarget] = useState(null);
  const isDarkTheme = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
  const lightboxTheme = isDarkTheme
    ? {
        overlayBackground: 'linear-gradient(135deg, rgba(0, 0, 0, 0.92) 0%, rgba(15, 23, 42, 0.95) 100%)',
        panelBackground: '#ffffff',
        panelShadow: '0 25px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)',
        imageBackground: 'rgba(17,24,39,1)',
        infoBackground: 'linear-gradient(180deg, #0b1220 0%, #111827 100%)',
        infoBorder: '1px solid rgba(255, 255, 255, 0.08)',
        headingColor: '#ffffff',
        cardBackground: 'rgba(255, 255, 255, 0.04)',
        cardBorder: '1px solid rgba(255, 255, 255, 0.08)',
        labelColor: '#ffffff',
        valueColor: '#ffffff',
        dividerColor: '1px solid rgba(255, 255, 255, 0.12)',
        chipBackground: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
        chipColor: '#ffffff',
        closeBackground: 'rgba(0, 0, 0, 0.75)',
        closeBorder: '2px solid rgba(255, 255, 255, 0.1)',
        closeColor: '#ffffff',
        mobileBorderTop: '#e2e8f0'
      }
    : {
        overlayBackground: 'linear-gradient(135deg, rgba(248, 250, 252, 0.94) 0%, rgba(226, 232, 240, 0.96) 100%)',
        panelBackground: '#ffffff',
        panelShadow: '0 25px 60px rgba(15, 23, 42, 0.18), 0 0 0 1px rgba(15, 23, 42, 0.06)',
        imageBackground: '#f3f4f6',
        infoBackground: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        infoBorder: '1px solid rgba(15, 23, 42, 0.08)',
        headingColor: '#0f172a',
        cardBackground: 'rgba(255, 255, 255, 0.95)',
        cardBorder: '1px solid rgba(15, 23, 42, 0.08)',
        labelColor: '#334155',
        valueColor: '#0f172a',
        dividerColor: '1px solid rgba(15, 23, 42, 0.08)',
        chipBackground: '#e0e7ff',
        chipColor: '#1e3a8a',
        closeBackground: 'rgba(255, 255, 255, 0.92)',
        closeBorder: '2px solid rgba(15, 23, 42, 0.08)',
        closeColor: '#0f172a',
        mobileBorderTop: '#e2e8f0'
      };

  useEffect(() => {
    setImageLoaded(false);
  }, [image]);

  useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  if (!portalTarget) {
    return null;
  }

  return createPortal(
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="show-gallery-lightbox-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: lightboxTheme.overlayBackground,
        backdropFilter: 'blur(12px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '2rem',
        animation: 'fadeIn 0.3s ease-out'
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        className="lightbox-content"
        style={{
          position: 'relative',
          maxWidth: '1400px',
          width: '92vw',
          maxHeight: '92vh',
          background: lightboxTheme.panelBackground,
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: lightboxTheme.panelShadow,
          animation: 'slideUp 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
          display: 'flex',
          flexDirection: 'row'
        }}
      >
        <div
          className="lightbox-image"
          style={{
            flex: '1',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: lightboxTheme.imageBackground,
            minHeight: '400px'
          }}
        >
          <img
            src={image.src}
            alt={image.alt}
            loading="eager"
            decoding="async"
            onLoad={() => setImageLoaded(true)}
            style={{
              display: 'block',
              maxWidth: '100%',
              maxHeight: '90vh',
              objectFit: 'contain',
              opacity: imageLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease-out'
            }}
          />

          {!imageLoaded && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '40px',
                height: '40px',
                border: '3px solid #e2e8f0',
                borderTop: '3px solid #667eea',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}
            />
          )}
        </div>

        <div
          className="lightbox-info"
          style={{
            flex: '0 0 380px',
            padding: '3.5rem 2.5rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '2.5rem',
            background: lightboxTheme.infoBackground,
            borderLeft: lightboxTheme.infoBorder,
            overflowY: 'auto',
            maxHeight: '92vh',
            position: 'relative'
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(to right, #3b82f6, #8b5cf6, #ec4899)'
            }}
          />

          <div>
            <h3
              id="show-gallery-lightbox-title"
              style={{
                fontSize: '2rem',
                fontWeight: 800,
                margin: '0 0 1.75rem 0',
                color: lightboxTheme.headingColor,
                lineHeight: 1.2,
                letterSpacing: '-0.02em'
              }}
            >
              {image.showName}
            </h3>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.5rem',
                padding: '1.5rem',
                background: lightboxTheme.cardBackground,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.18)',
                border: lightboxTheme.cardBorder
              }}
            >
              <div>
                <span
                  style={{
                    display: 'block',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: lightboxTheme.labelColor,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginBottom: '0.6rem'
                  }}
                >
                  Image
                </span>
                <span
                  style={{
                    fontSize: '1.15rem',
                    fontWeight: 600,
                    color: lightboxTheme.valueColor,
                    display: 'block'
                  }}
                >
                  {image.title}
                </span>
              </div>

              <div
                style={{
                  borderTop: lightboxTheme.dividerColor,
                  paddingTop: '1.5rem'
                }}
              >
                <span
                  style={{
                    display: 'block',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: lightboxTheme.labelColor,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginBottom: '0.6rem'
                  }}
                >
                  Category
                </span>
                <span
                  style={{
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    display: 'inline-block',
                    padding: '0.5rem 1rem',
                    background: lightboxTheme.chipBackground,
                    color: lightboxTheme.chipColor,
                    borderRadius: '8px'
                  }}
                >
                  {image.category}
                </span>
              </div>

              {image.photographer ? (
                <div>
                  <span
                    style={{
                      display: 'block',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: lightboxTheme.labelColor,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: '0.5rem'
                    }}
                  >
                    Photographer
                  </span>
                  <span
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      color: lightboxTheme.valueColor
                    }}
                  >
                    {image.photographer}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <button
          onClick={onClose}
          aria-label="Close gallery"
          style={{
            position: 'absolute',
            top: '1.5rem',
            right: '1.5rem',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: lightboxTheme.closeBackground,
            backdropFilter: 'blur(8px)',
            border: lightboxTheme.closeBorder,
            color: lightboxTheme.closeColor,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.75rem',
            fontWeight: 300,
            transition: 'all 0.25s ease-out',
            zIndex: 10,
            lineHeight: 1
          }}
          onMouseEnter={(event) => {
            event.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6, #8b5cf6)';
            event.currentTarget.style.transform = 'scale(1.1) rotate(90deg)';
            event.currentTarget.style.borderColor = isDarkTheme ? 'rgba(255, 255, 255, 0.3)' : 'rgba(59, 130, 246, 0.32)';
            event.currentTarget.style.color = '#ffffff';
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.background = lightboxTheme.closeBackground;
            event.currentTarget.style.transform = 'scale(1) rotate(0deg)';
            event.currentTarget.style.borderColor = isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.08)';
            event.currentTarget.style.color = lightboxTheme.closeColor;
          }}
        >
          ×
        </button>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }

            @keyframes slideUp {
              from {
                opacity: 0;
                transform: translateY(30px) scale(0.95);
              }
              to {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }

            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }

            @media (max-width: 768px) {
              .lightbox-content {
                flex-direction: column !important;
                max-height: 100vh !important;
              }

              .lightbox-info {
                flex: 1 !important;
                border-left: none !important;
                border-top: 1px solid ${lightboxTheme.mobileBorderTop} !important;
                padding: 2rem 1.5rem !important;
                max-height: 50vh !important;
              }

              .lightbox-image {
                flex: 1 !important;
                max-height: 50vh !important;
              }
            }

            @media (max-width: 480px) {
              .lightbox-info {
                padding: 1.5rem 1rem !important;
                font-size: 0.9rem !important;
              }

              .lightbox-info h3 {
                font-size: 1.4rem !important;
              }
            }
          `
        }}
      />
    </div>,
    portalTarget
  );
});

export default ShowGalleryCarousel;